import { NavLink, useLocation } from "react-router-dom";
import { useContext, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { isFreelance } from "../../utils/constants/accountType";
import { getSettingsSidebarItems } from "../../pages/admin/Settings/settingsMenu";
import {
  FiHome,
  FiSettings,
  FiChevronDown,
  FiChevronRight,
  FiUsers,
  FiUserCheck,
  FiBriefcase,
  FiBarChart2,
  FiFileText,
  FiDollarSign,
  FiCreditCard,
  FiPackage,
  FiFolder,
  FiCheckSquare,
  FiClipboard,
} from "react-icons/fi";

const COLLAPSED_W = 72;

const ITEM_BASE =
  "flex items-center rounded-xl text-sm font-medium transition-colors duration-150";
const ITEM_IDLE = "text-gray-600 hover:bg-gray-100/80 hover:text-gray-900";
const ITEM_ACTIVE = "bg-brand-50 text-brand-700";
const ITEM_ACTIVE_SOLID = "bg-brand-600 text-white shadow-card";
const SUB_IDLE = "text-gray-500 hover:bg-gray-100/80 hover:text-gray-800";
const SUB_ACTIVE = "bg-brand-50 text-brand-700 font-semibold";

const SidebarShell = ({ isOpen, onToggle, children }) => (
  <aside
    className={`fixed bottom-0 left-0 top-16 z-20 flex flex-col border-r border-gray-200 bg-white
      transition-all duration-300 ease-in-out
      ${isOpen ? "w-64 overflow-y-auto overflow-x-hidden" : "w-[4.5rem] overflow-visible"}`}
  >
    {/* Top toggle */}
    <div
      className={`flex shrink-0 border-b border-gray-100
      ${isOpen ? "items-center justify-between px-4 py-3" : "items-center justify-center px-2 py-3"}`}
    >
      {isOpen && (
        <span className="select-none text-[10px] font-bold uppercase tracking-widest text-gray-400">
          Navigation
        </span>
      )}
      <button
        onClick={onToggle}
        title={isOpen ? "Collapse sidebar" : "Expand sidebar"}
        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
      >
        {isOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
      </button>
    </div>

    {children}
  </aside>
);

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const isVendor = user?.roleSlug === "vendor";
  const isVendorManager = user?.roleSlug === "vendor_manager";
  const isProjectManager = user?.roleSlug === "project_manager";
  const isAccountManager = user?.roleSlug === "account_manager";
  const freelance = isFreelance(user);
  const isAdminSetupIncomplete =
    user?.roleSlug === "administrator" && !user?.setup_completed;

  const [expandedCategories, setExpandedCategories] = useState(() => {
    try {
      const saved = localStorage.getItem("sidebar-expanded");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [flyout, setFlyout] = useState(null);
  const flyoutTimer = useRef(null);

  const financesVisible = user?.vendor_flags?.finances_visible ?? false;

  // The label opens the Settings page; the chevron expands the same menu here.
  const adminSettingsLink = {
    label: "Settings", icon: FiSettings, to: "/settings", end: true, hasSubcategories: true,
    subcategories: getSettingsSidebarItems(user),
  };

  const vendorLinks = [
    { to: "/dashboard", icon: FiBriefcase, label: "Dashboard" },
    { to: "/jobs",      icon: FiPackage,   label: "Jobs"      },
    { to: "/vendor/agreement", icon: FiCheckSquare, label: "Agreement" },
    ...(financesVisible
      ? [
          { to: "/vendor/receivables", icon: FiDollarSign, label: "Receivables" },
          { to: "/vendor/invoices",    icon: FiFileText,   label: "Invoices"    },
          { to: "/vendor/payments",    icon: FiCreditCard, label: "Payments"    },
        ]
      : []),
  ];

  const adminLinks = [
    { label: "Dashboard",  icon: FiHome,      to: "/",           hasSubcategories: false },
    { label: "Quotations", icon: FiClipboard, to: "/quotations", hasSubcategories: false },
    { label: "Projects",   icon: FiFolder,    to: "/projects",   hasSubcategories: false },
    { label: "Jobs",       icon: FiPackage,   to: "/jobs",       hasSubcategories: false },
    {
      label: "Clients", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Clients",    to: "/clients", end: true },
        { label: "New Client",   to: "/clients/create-client" },
        { label: "Price List",   to: "/clients/price-list" },
        { label: "Client Pools", to: "/client-pools" },
      ],
    },
    {
      label: "Vendors", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendors",      to: "/vendors", end: true },
        { label: "New Vendor",   to: "/vendors/create-vendor" },
        { label: "Applications", to: "/vendor/applications" },
        { label: "Price List",   to: "/vendors/price-list" },
      ],
    },
    { label: "Managers", icon: FiUserCheck, to: "/managers", hasSubcategories: false },
    {
      label: "Finances", icon: FiDollarSign, hasSubcategories: true,
      subcategories: [
        { label: "Vendor Payables",    to: "/vendor/payables" },
        { label: "Vendor Invoices",    to: "/vendor/invoices" },
        { label: "Vendor Payments",    to: "/vendor/payments" },
        { label: "Client Receivables", to: "/client/receivables" },
        { label: "Client Invoices",    to: "/client/invoices" },
        { label: "Client Payments",    to: "/client/payments" },
      ],
    },
    { label: "Files", icon: FiFileText, to: "/files", hasSubcategories: false },
    adminSettingsLink,
  ];

  const freelanceAdminLinks = [
    { label: "Dashboard",  icon: FiHome,      to: "/",           hasSubcategories: false },
    { label: "Quotations", icon: FiClipboard, to: "/quotations", hasSubcategories: false },
    { label: "Projects",   icon: FiFolder,    to: "/projects",   hasSubcategories: false },
    {
      label: "Clients", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Clients",    to: "/clients", end: true },
        { label: "New Client", to: "/clients/create-client" },
        { label: "Price List", to: "/clients/price-list" },
      ],
    },
    {
      label: "Finances", icon: FiDollarSign, hasSubcategories: true,
      subcategories: [
        { label: "Receivables", to: "/client/receivables" },
        { label: "Invoices",    to: "/client/invoices" },
        { label: "Payments",    to: "/client/payments" },
      ],
    },
    { label: "Files",   icon: FiFileText,  to: "/files",   hasSubcategories: false },
    adminSettingsLink,
  ];

  const vendorManagerLinks = [
    { label: "To Dos", icon: FiCheckSquare, to: "/todos", hasSubcategories: false },
    {
      label: "Vendors", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendors",      to: "/vendors", end: true    },
        { label: "New Vendor",   to: "/vendors/create-vendor" },
        { label: "Applications", to: "/vendor/applications"   },
        { label: "Contacts",     to: "/vendor/contacts"       },
        { label: "Prices",       to: "/vendor/prices"         },
        { label: "Tags",         to: "/vendor/tags"           },
      ],
    },
    {
      label: "Settings", icon: FiSettings, hasSubcategories: true,
      subcategories: [
        { label: "Account",        to: "/settings/account" },
        { label: "Custom Codes",   to: "/settings/object-codes" },
        { label: "Legal Entities", to: "/settings/legal-entities" },
        {
          label: "Access Control",
          subcategories: [
            { label: "Roles",    to: "/settings/roles" },
            { label: "Managers", to: "/settings/managers" },
          ],
        },
        {
          label: "System Values",
          subcategories: [
            { label: "Language Pairs",  to: "/settings/system-values/language-pairs" },
            { label: "Services",        to: "/settings/system-values/services" },
            { label: "Specialization",  to: "/settings/system-values/specializations" },
            { label: "Units",           to: "/settings/system-values/units" },
            { label: "Currencies",      to: "/settings/system-values/currencies" },
          ],
        },
        { label: "Taxes",         to: "/settings/taxes" },
        { label: "Vendor Portal", to: "/settings/vendor-portal" },
      ],
    },
  ];

  const projectManagerLinks = [
    { label: "Dashboard", icon: FiHome, to: "/", hasSubcategories: false },
    {
      label: "System Values", icon: FiBarChart2, to: "/system-values", hasSubcategories: true,
      subcategories: [
        { label: "Services",        to: "/system-values/services" },
        { label: "Language Pairs",  to: "/system-values/language-pairs" },
        { label: "Specializations", to: "/system-values/specializations" },
        { label: "Units",           to: "/system-values/units" },
        { label: "Currencies",      to: "/system-values/currencies" },
      ],
    },
    {
      label: "Vendor", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendor",   to: "/vendors", end: true },
        { label: "Payables", to: "/vendor/payables" },
        { label: "Invoice",  to: "/vendor/invoices" },
        { label: "Payment",  to: "/vendor/payments" },
      ],
    },
    {
      label: "Client", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Client",      to: "/clients", end: true },
        { label: "Create New",  to: "/clients/create-client" },
        { label: "Receivables", to: "/client/receivables" },
        { label: "Invoice",     to: "/client/invoices" },
        { label: "Payment",     to: "/client/payments" },
      ],
    },
    { label: "Quotation", icon: FiClipboard, to: "/quotations", hasSubcategories: false },
    { label: "Project", icon: FiFolder,  to: "/projects", hasSubcategories: false },
    { label: "Jobs",    icon: FiPackage, to: "/jobs",     hasSubcategories: false },
    {
      label: "Settings", icon: FiSettings, hasSubcategories: true,
      subcategories: [
        { label: "Account",      to: "/settings/account" },
        { label: "Custom Codes", to: "/settings/object-codes" },
      ],
    },
  ];

  const accountManagerLinks = [
    { label: "Dashboard", icon: FiHome, to: "/", hasSubcategories: false },
    {
      label: "Client", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Client",      to: "/clients", end: true },
        { label: "Receivables", to: "/client/receivables" },
        { label: "Invoice",     to: "/client/invoices" },
        { label: "Payment",     to: "/client/payments" },
      ],
    },
    {
      label: "Vendor", icon: FiUsers, hasSubcategories: true,
      subcategories: [
        { label: "Vendor",   to: "/vendors", end: true },
        { label: "Payables", to: "/vendor/payables" },
        { label: "Invoice",  to: "/vendor/invoices" },
        { label: "Payment",  to: "/vendor/payments" },
      ],
    },
  ];

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    localStorage.setItem("sidebar-open", next);
    if (next) setFlyout(null);
  };

  const toggleCategory = (label) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      localStorage.setItem("sidebar-expanded", JSON.stringify([...next]));
      return next;
    });
  };

  const collectTos = (subs) =>
    subs.flatMap((s) =>
      s.subcategories ? collectTos(s.subcategories) : s.to ? [s.to] : []
    );

  const isSubcategoryActive = (subs) =>
    collectTos(subs).some((to) => location.pathname.startsWith(to));

  const renderSubItems = (subs) =>
    subs.map((sub) =>
      sub.subcategories ? (
        <div key={sub.label}>
          <button
            type="button"
            onClick={() => toggleCategory(sub.label)}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors
              ${isSubcategoryActive(sub.subcategories) ? "text-brand-700 hover:bg-gray-100/80" : SUB_IDLE}`}
          >
            <FiChevronRight
              size={12}
              className={`-ml-1 shrink-0 transition-transform duration-200 ${
                expandedCategories.has(sub.label) ? "rotate-90" : ""
              }`}
            />
            <span className="flex-1">{sub.label}</span>
          </button>
          {expandedCategories.has(sub.label) && (
            <div className="mb-1 ml-[14px] mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
              {renderSubItems(sub.subcategories)}
            </div>
          )}
        </div>
      ) : (
        <NavLink
          key={sub.to}
          to={sub.to}
          end={sub.end}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors
             ${isActive ? SUB_ACTIVE : SUB_IDLE}`
          }
        >
          <span className="h-1 w-1 shrink-0 rounded-full bg-current" />
          {sub.label}
        </NavLink>
      )
    );

  const openFlyout = (e, item) => {
    if (isOpen) return;
    clearTimeout(flyoutTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({ item, top: rect.top, bottom: rect.bottom, centerY: rect.top + rect.height / 2 });
  };

  const flyoutPosition = (f) => {
    const left = COLLAPSED_W + 10;
    if (!f.item.hasSubcategories) {
      return { style: { top: f.centerY, left, transform: "translateY(-50%)" } };
    }
    if (f.centerY < window.innerHeight / 2) {
      return { style: { top: f.top, left }, maxHeight: window.innerHeight - f.top - 16 };
    }
    return { style: { bottom: window.innerHeight - f.bottom, left }, maxHeight: f.bottom - 80 };
  };

  const scheduleFlyoutClose = () => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), 150);
  };

  const cancelFlyoutClose = () => clearTimeout(flyoutTimer.current);

  const navLinks = isVendorManager
    ? vendorManagerLinks
    : isProjectManager
    ? projectManagerLinks
    : isAccountManager
    ? accountManagerLinks
    : freelance
    ? freelanceAdminLinks
    : adminLinks;

  useEffect(() => {
    const activeTrail = (items) => {
      const group = items.find(
        (item) => item.subcategories && isSubcategoryActive(item.subcategories)
      );
      return group ? [group.label, ...activeTrail(group.subcategories)] : [];
    };
    const trail = activeTrail(navLinks);
    if (!trail.length) return;
    setExpandedCategories((prev) => {
      if (trail.every((label) => prev.has(label))) return prev;
      const next = new Set([...prev, ...trail]);
      localStorage.setItem("sidebar-expanded", JSON.stringify([...next]));
      return next;
    });
  }, [location.pathname]);

  if (isAdminSetupIncomplete) {
    return (
      <SidebarShell isOpen={isOpen} onToggle={toggle}>
        <div className="mt-1 space-y-3 p-3">
          {isOpen && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Complete setup to unlock all features.
            </p>
          )}
          <NavLink
            to="/setup"
            className={({ isActive }) =>
              `${ITEM_BASE} gap-3 px-3 py-2.5 ${isActive ? ITEM_ACTIVE_SOLID : ITEM_IDLE}`
            }
          >
            <FiSettings size={18} className="shrink-0" />
            {isOpen && <span>Complete Setup</span>}
          </NavLink>
        </div>
      </SidebarShell>
    );
  }

  if (isVendor) {
    return (
      <SidebarShell isOpen={isOpen} onToggle={toggle}>
        <nav className="mt-1 space-y-1 p-3">
          {vendorLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onMouseEnter={(e) => openFlyout(e, link)}
              onMouseLeave={scheduleFlyoutClose}
              className={({ isActive }) =>
                `${ITEM_BASE}
                 ${isOpen ? "gap-3 px-3 py-2.5" : "justify-center px-2 py-3"}
                 ${isActive ? (isOpen ? ITEM_ACTIVE : ITEM_ACTIVE_SOLID) : ITEM_IDLE}`
              }
            >
              <link.icon size={18} className="shrink-0" />
              {isOpen && <span>{link.label}</span>}
            </NavLink>
          ))}
        </nav>
      </SidebarShell>
    );
  }

  const flyoutPos = flyout && flyoutPosition(flyout);

  return (
    <>
      <SidebarShell isOpen={isOpen} onToggle={toggle}>
        <nav className="mt-1 flex-1 space-y-1 p-3">
          {navLinks.map((item) => {
            const parentActive = item.hasSubcategories && isSubcategoryActive(item.subcategories);

            if (!isOpen) {
              const collapsedClasses = `${ITEM_BASE} justify-center px-2 py-3`;

              if (!item.to) {
                return (
                  <button
                    key={item.label}
                    type="button"
                    onMouseEnter={(e) => openFlyout(e, item)}
                    onMouseLeave={scheduleFlyoutClose}
                    className={`${collapsedClasses} w-full
                      ${parentActive ? ITEM_ACTIVE_SOLID : ITEM_IDLE}`}
                  >
                    <item.icon size={18} className="shrink-0" />
                  </button>
                );
              }

              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  onMouseEnter={(e) => openFlyout(e, item)}
                  onMouseLeave={scheduleFlyoutClose}
                  onClick={() => setFlyout(null)}
                  className={({ isActive }) =>
                    `${collapsedClasses}
                     ${isActive || parentActive ? ITEM_ACTIVE_SOLID : ITEM_IDLE}`
                  }
                >
                  <item.icon size={18} className="shrink-0" />
                </NavLink>
              );
            }

            /* Expanded view */
            return (
              <div key={item.label}>
                {item.hasSubcategories ? (
                  <>
                    <div
                      className={`${ITEM_BASE} ${parentActive ? ITEM_ACTIVE : ITEM_IDLE}`}
                    >
                      {item.to ? (
                        <>
                          <NavLink
                            to={item.to}
                            end={item.end}
                            onClick={() => {
                              setExpandedCategories((prev) => {
                                const next = new Set(prev);
                                next.delete(item.label);
                                localStorage.setItem("sidebar-expanded", JSON.stringify([...next]));
                                return next;
                              });
                            }}
                            className={({ isActive }) =>
                              `flex flex-1 items-center gap-3 px-3 py-2.5
                               ${isActive ? "font-semibold text-brand-700" : ""}`
                            }
                          >
                            <item.icon size={18} className="shrink-0" />
                            <span>{item.label}</span>
                          </NavLink>
                          <button
                            onClick={() => toggleCategory(item.label)}
                            className="py-2.5 pl-1 pr-3 transition-colors hover:text-brand-700"
                          >
                            <FiChevronDown
                              size={14}
                              className={`transition-transform duration-300 ${
                                expandedCategories.has(item.label) ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleCategory(item.label)}
                          className="flex w-full items-center text-left"
                        >
                          <span className="flex flex-1 items-center gap-3 px-3 py-2.5">
                            <item.icon size={18} className="shrink-0" />
                            <span>{item.label}</span>
                          </span>
                          <span className="py-2.5 pl-1 pr-3">
                            <FiChevronDown
                              size={14}
                              className={`transition-transform duration-300 ${
                                expandedCategories.has(item.label) ? "rotate-180" : ""
                              }`}
                            />
                          </span>
                        </button>
                      )}
                    </div>

                    {expandedCategories.has(item.label) && (
                      <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pb-1 pl-3">
                        {renderSubItems(item.subcategories)}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `${ITEM_BASE} gap-3 px-3 py-2.5 ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`
                    }
                  >
                    <item.icon size={18} className="shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>
      </SidebarShell>

      {/* Collapsed flyout / tooltip (portal) */}
      {!isOpen && flyout && createPortal(
        <div
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
          style={flyoutPos.style}
          className="fixed z-50"
        >
          {flyout.item.hasSubcategories ? (
            /* Subcategory dropdown */
            <div
              style={{ maxHeight: flyoutPos.maxHeight }}
              className="w-56 overflow-y-auto rounded-2xl border border-gray-200 bg-white py-1.5 shadow-pop"
            >
              <div className="mb-1 flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                <flyout.item.icon size={13} className="shrink-0 text-brand-600" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  {flyout.item.label}
                </span>
              </div>
              {flyout.item.subcategories.map((sub) => {
                if (sub.subcategories) {
                  return (
                    <div key={sub.label}>
                      <div className="px-4 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        {sub.label}
                      </div>
                      {sub.subcategories.map((child) => {
                        const isActive = child.end
                          ? location.pathname === child.to
                          : location.pathname.startsWith(child.to);
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={() => setFlyout(null)}
                            className={`flex items-center gap-2.5 py-2 pl-7 pr-4 text-sm transition-colors
                              ${isActive ? "bg-brand-50 font-semibold text-brand-700" : SUB_IDLE}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                isActive ? "bg-brand-600" : "bg-gray-300"
                              }`}
                            />
                            {child.label}
                          </NavLink>
                        );
                      })}
                    </div>
                  );
                }
                const isActive = sub.end
                  ? location.pathname === sub.to
                  : location.pathname.startsWith(sub.to);
                return (
                  <NavLink
                    key={sub.to}
                    to={sub.to}
                    onClick={() => setFlyout(null)}
                    className={`flex items-center gap-2.5 px-4 py-2 text-sm transition-colors
                      ${isActive ? "bg-brand-50 font-semibold text-brand-700" : SUB_IDLE}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                        isActive ? "bg-brand-600" : "bg-gray-300"
                      }`}
                    />
                    {sub.label}
                  </NavLink>
                );
              })}
            </div>
          ) : (
            /* Simple label tooltip */
            <div className="flex items-center">
              {/* arrow */}
              <div className="h-0 w-0 border-b-[6px] border-r-[7px] border-t-[6px] border-b-transparent border-r-gray-900 border-t-transparent" />
              <div className="whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white shadow-pop">
                {flyout.item.label}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default Sidebar;